import { Drawer } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import * as Yup from "yup";
import { FormInput } from "../../../components/atoms/Input";
import { ButtonT1 } from "../../../components/atoms/Button";
import { openCreateInNewTab } from "../../../components/molecules/quickCreate";
import { QuickCreateSelect } from "../../shared/quickCreate";
import { PortalFooter } from "../../../components/molecules/Footer";
import { PortalHeader } from "../../../components/molecules/Header";
import { portalDrawerAntHeaderCss } from "../../../components/molecules/Header/Header.styles";
import {
  FormControlInput,
  FormControlSearchableSelectAPI,
} from "../../../components/organisms/Form";
import { FormControlDate, FormControlSelect } from "../../../components/organisms/Form/control";
import { FormControlError } from "../../../components/atoms/FormFiledError";
import { FormLabel } from "../../../components/atoms/label/label";
import { ManageGeneralFormProvider } from "../../../components/organisms/Provider/ManageGeneralFormProvider";
import { useNotification } from "../../../hooks/useNotification";
import { useSelectContext } from "../../../hooks/useSelectContext";
import { PortalFooterCard } from "../../../components/atoms/Footer";
import {
  useCreatePrePaymentMutation,
  useUpdatePrePaymentMutation,
} from "../../../store/apiSlice/invoiceApiSlice";
import { useLazyGetPurchaseOrderForPrepaymentQuery } from "../../../store/apiSlice/purchaseOrderApiSlice";
import { ReactComponent as DeleteDanger } from "../../newLayout/assets/svgIcon/shipment/DeleteDanger.svg";
import { StyledDeleteRowButton } from "../../shared/orderLineForm/components/createPurchaseOrderPageV2.styles";
import {
  allocationsChanged,
  buildAutoAllocatedRows,
  resolvePoOutstanding,
} from "./prepaymentAllocationHelpers";

const emptyToUndefined = (value, originalValue) =>
  originalValue === "" || originalValue === null ? undefined : value;

const isDayjsLike = value =>
  value != null && typeof value === "object" && typeof value.isValid === "function";

const isValidPaymentDate = value => {
  if (value == null || value === "") return false;
  if (isDayjsLike(value)) return value.isValid();
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  return dayjs(value).isValid();
};

const formatPaymentDate = value => {
  if (isDayjsLike(value)) return value.format("YYYY-MM-DD");
  if (value instanceof Date) return dayjs(value).format("YYYY-MM-DD");
  return dayjs(value).format("YYYY-MM-DD");
};

const allocationRowSchema = Yup.object().shape({
  purchase_order_id: Yup.number()
    .transform(emptyToUndefined)
    .typeError("Purchase order is required")
    .required("Purchase order is required"),
  allocated_amount: Yup.number()
    .transform(emptyToUndefined)
    .typeError("Allocated amount is required")
    .required("Allocated amount is required")
    .positive("Amount must be greater than 0"),
});

const recordPaymentSchema = Yup.object().shape({
  supplier_id: Yup.number().required("Supplier is required"),
  account_id: Yup.number().required("Account is required"),
  reference: Yup.string().required("Reference is required"),
  date_paid: Yup.mixed().test("payment-date", "Payment date is required", value =>
    isValidPaymentDate(value),
  ),
  currency_id: Yup.number().required("Currency is required"),
  amount: Yup.number().required("Amount is required").positive("Amount must be greater than 0"),
  bank_account_dummy_id: Yup.string().required("Bank account is required"),
  payment_type_id: Yup.string().required("Payment type is required"),
  allocations: Yup.array().of(allocationRowSchema).min(1, "At least one allocation is required"),
});

const touchAllRecordPaymentFields = values => ({
  supplier_id: true,
  account_id: true,
  reference: true,
  date_paid: true,
  currency_id: true,
  amount: true,
  bank_account_dummy_id: true,
  payment_type_id: true,
  allocations: values.allocations.map(() => ({
    purchase_order_id: true,
    allocated_amount: true,
  })),
});

const collectValidationMessages = errors => {
  const messages = [];
  const walk = value => {
    if (!value) return;
    if (typeof value === "string") {
      messages.push(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value === "object") {
      Object.values(value).forEach(walk);
    }
  };
  walk(errors);
  return messages;
};

function AllocationAmountField({ index, readOnly, onUserEdit }) {
  const {
    formik: { getFieldProps, touched, errors },
  } = useSelectContext("general");
  const name = `allocations[${index}].allocated_amount`;
  const fieldProps = getFieldProps(name);
  const rowTouched = touched?.allocations?.[index]?.allocated_amount;
  const rowError = errors?.allocations?.[index]?.allocated_amount;

  return (
    <>
      {index === 0 ? (
        <FormLabel className="req" htmlFor={name}>
          Allocated amount
        </FormLabel>
      ) : (
        <AllocationLabelSpacer aria-hidden="true" />
      )}
      <FormInput
        {...fieldProps}
        aria-describedby={`${name}-error`}
        id={name}
        placeholder="Amount"
        readOnly={readOnly}
        type="number"
        step={0.01}
        onChange={event => {
          if (!readOnly) {
            onUserEdit(index);
          }
          fieldProps.onChange(event);
        }}
      />
      <FormControlError id={`${name}-error`} errorMsg={rowTouched && rowError} />
    </>
  );
}

function RecordPaymentAllocationRows({ setManualRowIndices, onRemoveRow, readOnly = false }) {
  const {
    formik: { values, setFieldValue },
  } = useSelectContext("general");
  const poSelectionParams = useMemo(() => {
    if (!values.supplier_id) return undefined;
    const search = new URLSearchParams();
    search.set("supplier_id", String(values.supplier_id));
    if (values.currency_id) {
      search.set("currency_id", String(values.currency_id));
    }
    return search.toString();
  }, [values.supplier_id, values.currency_id]);

  const poSelectDisabled = readOnly || !values.supplier_id;
  const poPlaceholder = !values.supplier_id
    ? "Select supplier first"
    : !values.currency_id
      ? "Select currency first"
      : "Select PO";

  const markManual = useCallback(
    index => {
      setManualRowIndices(prev => new Set(prev).add(index));
    },
    [setManualRowIndices],
  );

  return (
    <>
      <div className="col-12 mb-2">
        <label className="form-label fw-semibold">Allocations</label>
      </div>
      {values.allocations.map((_, index) => {
        const canRemove = !readOnly && values.allocations.length > 1;

        return (
          <div className="col-12 mb-3" key={`alloc-${index}`}>
            <AllocationRow $canRemove={canRemove} className="allocation-row">
              <div className="allocation-field">
                {index !== 0 && <AllocationLabelSpacer aria-hidden="true" />}
                <FormControlSearchableSelectAPI
                  moduleName="purchaseOrder"
                  label={index === 0 ? "Purchase Order" : ""}
                  name={`allocations[${index}].purchase_order_id`}
                  placeholder={poPlaceholder}
                  context="general"
                  required
                  params={poSelectionParams}
                  skip={!values.supplier_id || !values.currency_id}
                  disabled={poSelectDisabled || !values.currency_id}
                  createLabel="Create purchase order"
                  onCreate={() => openCreateInNewTab("/orders/purchase/create")}
                  handleOnChange={() => setFieldValue(`allocations[${index}].allocated_amount`, "")}
                />
              </div>
              <div className="allocation-field">
                <AllocationAmountField index={index} readOnly={readOnly} onUserEdit={markManual} />
              </div>
              {canRemove && (
                <div className="allocation-row-delete">
                  <AllocationLabelSpacer aria-hidden="true" />
                  <StyledDeleteRowButton
                    className="allocation-row-delete-button"
                    type="button"
                    title="Remove order"
                    aria-label="Remove order from payment"
                    onClick={() => onRemoveRow(index)}
                  >
                    <DeleteDanger />
                  </StyledDeleteRowButton>
                </div>
              )}
            </AllocationRow>
          </div>
        );
      })}
    </>
  );
}

/**
 * Record a supplier payment with PO allocations (shared by PO detail, PO list, prepayments list).
 */
export function RecordPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  initialValues,
  prepaymentId = null,
  readOnly = false,
  title = "Record Payment",
  lockSupplier = false,
  lockCurrency = false,
}) {
  const notify = useNotification();
  const [createPrePayment, { isLoading: isCreating }] = useCreatePrePaymentMutation();
  const [updatePrePayment, { isLoading: isUpdating }] = useUpdatePrePaymentMutation();
  const isLoading = isCreating || isUpdating;
  const isEditMode = Boolean(prepaymentId);
  const [fetchPurchaseOrderDetail] = useLazyGetPurchaseOrderForPrepaymentQuery();
  const [poOutstandingById, setPoOutstandingById] = useState({});
  const [manualRowIndices, setManualRowIndices] = useState(() => new Set());
  const prevPoIdsRef = useRef([]);
  const pendingPoIdsRef = useRef(new Set());
  const lookupEpochRef = useRef(0);
  const prevSupplierCurrencyRef = useRef(null);

  const { formInitialValues, seedOutstandingById } = useMemo(() => {
    const { poOutstandingById: seeded = {}, ...rest } = initialValues || {};
    return {
      formInitialValues: rest,
      seedOutstandingById: seeded,
    };
  }, [initialValues]);

  useEffect(() => {
    if (!isOpen) {
      prevSupplierCurrencyRef.current = null;
      return;
    }
    setPoOutstandingById(seedOutstandingById || {});
    setManualRowIndices(new Set());
    prevPoIdsRef.current = [];
    pendingPoIdsRef.current.clear();
    lookupEpochRef.current += 1;
    // Capture the values when this drawer opens or switches to another payment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prepaymentId]);

  const mergedInitialValues = useMemo(
    () => ({
      supplier_id: "",
      account_id: "",
      reference: "",
      date_paid: dayjs(),
      currency_id: "",
      amount: "",
      bank_account_dummy_id: "",
      payment_type_id: "deposit",
      allocations: [{ purchase_order_id: "", allocated_amount: "" }],
      ...formInitialValues,
    }),
    [formInitialValues],
  );

  const formik = useFormik({
    initialValues: mergedInitialValues,
    validationSchema: recordPaymentSchema,
    enableReinitialize: false,
    validateOnBlur: false,
    validateOnChange: true,
    onSubmit: async values => {
      if (readOnly) return;

      const { date_paid, allocations, ...leftValues } = values;
      const payload = {
        ...leftValues,
        date_paid: formatPaymentDate(date_paid),
        allocations: allocations.map(row => ({
          purchase_order_id: Number(row.purchase_order_id),
          allocated_amount: String(row.allocated_amount),
        })),
      };

      const totalAllocated = allocations.reduce(
        (sum, row) => sum + Number(row.allocated_amount || 0),
        0,
      );
      if (totalAllocated > Number(values.amount)) {
        notify({
          type: "error",
          message: "Total allocated amount cannot exceed the payment amount.",
        });
        return;
      }

      try {
        if (isEditMode) {
          await updatePrePayment({ id: prepaymentId, ...payload }).unwrap();
          notify({ type: "success", message: "Payment has been updated successfully." });
        } else {
          await createPrePayment(payload).unwrap();
          notify({ type: "success", message: "Payment has been recorded successfully." });
        }
        formik.resetForm();
        onSuccess?.();
        onClose?.();
      } catch (error) {
        const data = error?.data;
        const messages = [];
        if (data && typeof data === "object") {
          Object.values(data).forEach(value => {
            if (Array.isArray(value)) messages.push(value.join(" "));
            else if (typeof value === "string") messages.push(value);
          });
        }
        notify({
          type: "error",
          message: messages.length
            ? messages.join(" | ")
            : isEditMode
              ? "Failed to update payment."
              : "Failed to record payment.",
        });
      }
    },
  });

  useEffect(() => {
    if (isOpen) {
      formik.resetForm({ values: mergedInitialValues });
      if (isEditMode) {
        setManualRowIndices(new Set(mergedInitialValues.allocations.map((_, index) => index)));
      }
      prevSupplierCurrencyRef.current = {
        supplier_id: mergedInitialValues.supplier_id,
        currency_id: mergedInitialValues.currency_id,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prepaymentId]);

  useEffect(() => {
    if (!isOpen || readOnly) return;

    const { supplier_id, currency_id, allocations } = formik.values;
    const prev = prevSupplierCurrencyRef.current;
    if (prev != null && (prev.supplier_id !== supplier_id || prev.currency_id !== currency_id)) {
      formik.setFieldValue(
        "allocations",
        allocations.map(row => ({ ...row, purchase_order_id: "" })),
      );
      setPoOutstandingById({});
      setManualRowIndices(new Set());
      prevPoIdsRef.current = [];
      pendingPoIdsRef.current.clear();
      lookupEpochRef.current += 1;
    }
    prevSupplierCurrencyRef.current = { supplier_id, currency_id };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, readOnly, formik.values.supplier_id, formik.values.currency_id]);

  const addAllocationRow = () => {
    formik.setFieldValue("allocations", [
      ...formik.values.allocations,
      { purchase_order_id: "", allocated_amount: "" },
    ]);
  };

  const removeAllocationRow = index => {
    if (formik.values.allocations.length <= 1) return;
    formik.setFieldValue(
      "allocations",
      formik.values.allocations.filter((_, rowIndex) => rowIndex !== index),
    );
    setManualRowIndices(prev => {
      const next = new Set();
      prev.forEach(rowIndex => {
        if (rowIndex < index) next.add(rowIndex);
        else if (rowIndex > index) next.add(rowIndex - 1);
      });
      return next;
    });
  };

  const allocationValues = formik.values.allocations;

  useEffect(() => {
    allocationValues.forEach((row, index) => {
      const prevPoId = prevPoIdsRef.current[index];
      if (prevPoId !== row.purchase_order_id) {
        setManualRowIndices(prev => {
          if (!prev.has(index)) return prev;
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }
    });
    prevPoIdsRef.current = allocationValues.map(row => row.purchase_order_id);
  }, [allocationValues]);

  useEffect(() => {
    if (!isOpen) return;
    const poIds = formik.values.allocations
      .map(row => Number(row.purchase_order_id))
      .filter(Boolean);

    poIds.forEach(poId => {
      if (poOutstandingById[poId] != null || pendingPoIdsRef.current.has(poId)) return;
      pendingPoIdsRef.current.add(poId);
      const lookupEpoch = lookupEpochRef.current;
      fetchPurchaseOrderDetail(poId)
        .unwrap()
        .then(data => {
          if (lookupEpoch !== lookupEpochRef.current) return;
          const outstanding = resolvePoOutstanding(data);
          if (outstanding == null) return;
          setPoOutstandingById(prev => ({ ...prev, [poId]: outstanding }));
        })
        .catch(() => {})
        .finally(() => {
          if (lookupEpoch === lookupEpochRef.current) pendingPoIdsRef.current.delete(poId);
        });
    });
  }, [isOpen, formik.values.allocations, fetchPurchaseOrderDetail, poOutstandingById]);

  const allocationPoKey = formik.values.allocations
    .map(row => row.purchase_order_id || "")
    .join("|");
  const manualRowsKey = [...manualRowIndices].sort((a, b) => a - b).join(",");
  const manualAllocationKey = formik.values.allocations
    .map((row, index) => (manualRowIndices.has(index) ? String(row.allocated_amount ?? "") : ""))
    .join("|");

  useEffect(() => {
    if (isEditMode) return;

    const current = formik.values.allocations;
    const nextRows = buildAutoAllocatedRows({
      allocations: current,
      paymentAmount: formik.values.amount,
      poOutstandingById,
      manualRowIndices,
    });

    if (allocationsChanged(current, nextRows)) {
      formik.setFieldValue("allocations", nextRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    formik.values.amount,
    allocationPoKey,
    poOutstandingById,
    manualRowsKey,
    manualAllocationKey,
  ]);

  const handleApply = async () => {
    if (readOnly) return;
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(touchAllRecordPaymentFields(formik.values), true);
      const messages = collectValidationMessages(errors);
      notify({
        type: "error",
        message: messages[0] || "Please complete all required fields before recording the payment.",
      });
      return;
    }
    formik.handleSubmit();
  };

  if (!isOpen) return null;

  return (
    <>
      <RecordPaymentDrawerTheme />
      <Drawer
        open={isOpen}
        title={<PortalHeader title={title} onClose={() => !isLoading && onClose()} />}
        rootClassName="record-payment-drawer"
        placement="right"
        width="min(100vw, max(560px, 42vw))"
        styles={{
          header: { padding: 0 },
          body: { padding: 0, overflow: "auto" },
          footer: { padding: 0 },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(1px)" },
        }}
        onClose={() => !isLoading && onClose()}
        closable={false}
        maskClosable={false}
        keyboard={!isLoading}
        destroyOnClose
        footer={
          readOnly ? (
            <PortalFooterCard className="for-modal">
              <ButtonT1 title="Close" $variant="secondary" onClickCallback={onClose} />
            </PortalFooterCard>
          ) : (
            <PortalFooter
              className="for-modal"
              isDisabled={isLoading}
              onApply={handleApply}
              onCancel={onClose}
              applyTitle={isEditMode ? "Save Payment" : "Record Payment"}
              isLoading={isLoading}
            />
          )
        }
      >
        <ManageGeneralFormProvider formik={formik}>
          <DrawerContent>
            <div className="row g-0">
              <div className="col-12 mb-4">
                <QuickCreateSelect
                  createKind="supplier"
                  moduleName="supplier"
                  label="Supplier"
                  name="supplier_id"
                  placeholder="Select Supplier"
                  context="general"
                  required
                  disabled={readOnly || lockSupplier}
                />
              </div>
              <div className="col-12 mb-4">
                <QuickCreateSelect
                  createKind="chartAccount"
                  moduleName="chartAccount"
                  label="Account"
                  name="account_id"
                  placeholder="Select Account"
                  context="general"
                  required
                  disabled={readOnly}
                />
              </div>
              <div className="col-6 mb-4 pe-2">
                <FormControlSelect
                  optionKey="prepaymentType"
                  label="Payment Type"
                  name="payment_type_id"
                  placeholder="Select type"
                  required
                  context="general"
                  disabled={readOnly}
                />
              </div>
              <div className="col-6 mb-4 ps-2">
                <FormControlSelect
                  optionKey="bankAccount"
                  label="Bank Account"
                  name="bank_account_dummy_id"
                  placeholder="Select Bank Account"
                  required
                  context="general"
                  disabled={readOnly}
                />
              </div>
              <div className="col-12 mb-4">
                <FormControlInput
                  name="reference"
                  type="text"
                  label="Reference"
                  placeholder="Enter Reference"
                  required
                  context="general"
                  disabled={readOnly}
                />
              </div>
              <div className="col-6 mb-4 pe-2">
                <FormControlDate
                  label="Payment Date"
                  name="date_paid"
                  placeholder="Select Payment Date"
                  required
                  context="general"
                  disabled={readOnly}
                />
              </div>
              <div className="col-6 mb-4 ps-2">
                <FormControlSearchableSelectAPI
                  moduleName="currency"
                  label="Currency"
                  name="currency_id"
                  placeholder="Select Currency"
                  required
                  context="general"
                  disabled={readOnly || lockCurrency}
                />
              </div>
              <div className="col-12 mb-4">
                <FormControlInput
                  name="amount"
                  type="number"
                  label="Payment Amount"
                  placeholder="Enter Amount"
                  required
                  context="general"
                  step={0.01}
                  disabled={readOnly}
                />
              </div>
              <RecordPaymentAllocationRows
                readOnly={readOnly}
                setManualRowIndices={setManualRowIndices}
                onRemoveRow={removeAllocationRow}
              />
              {!readOnly && (
                <div className="col-12 mb-3">
                  <ButtonT1
                    title="Add more orders to this payment"
                    $variant="primaryLight"
                    $size="small"
                    type="button"
                    onClickCallback={addAllocationRow}
                  />
                </div>
              )}
            </div>
          </DrawerContent>
        </ManageGeneralFormProvider>
      </Drawer>
    </>
  );
}

const DrawerContent = styled.div`
  padding: 16px 20px 24px;
  color: var(--GREY, #4a4a68);
  font-family: Inter, sans-serif;
`;

/** Matches FormLabel line-height (20px) + margin-bottom (4px). */
const AllocationLabelSpacer = styled.div`
  flex-shrink: 0;
  height: 24px;
`;

const AllocationRow = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: ${({ $canRemove }) => ($canRemove ? "1fr 1fr auto" : "1fr 1fr")};

  .allocation-field {
    min-width: 0;
  }

  .allocation-row-delete {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    width: 32px;
  }
`;

const RecordPaymentDrawerTheme = createGlobalStyle`
  .record-payment-drawer .ant-drawer-content {
    background: #fff;
    border-left: 1px solid var(--PEARL, #ecf1f4);
    color: var(--GREY, #4a4a68);
    font-family: Inter, sans-serif;
  }
  .record-payment-drawer .ant-drawer-header {
    ${portalDrawerAntHeaderCss}
  }
  .record-payment-drawer .ant-drawer-title {
    font-family: inherit;
    width: 100%;
  }
  .record-payment-drawer .ant-drawer-footer {
    border-top: 1px solid var(--PEARL, #ecf1f4);
  }
  .record-payment-drawer .for-modal {
    border-top: none;
  }
  .record-payment-drawer .allocation-row .ant-select {
    height: 32px;
    margin-bottom: 4px;
  }
  .record-payment-drawer .allocation-row .ant-select .ant-select-selector {
    align-items: center;
    height: 32px !important;
    min-height: 32px !important;
  }
  .record-payment-drawer .allocation-row .allocation-field input[type="number"] {
    margin-bottom: 4px;
  }
  .record-payment-drawer .allocation-row-delete-button {
    align-items: center;
    display: flex;
    flex-shrink: 0;
    height: 32px;
    justify-content: center;
    margin: 0;
    padding: 0;
    width: 32px;
  }
`;
